/**
 * Custom Firebase Action Handler
 *
 * Processes Firebase email action links (verify email, reset password)
 * in-app instead of relying on Firebase's default handler page.
 *
 * URL pattern: /auth/action?mode=verifyEmail&oobCode=XXX&continueUrl=YYY
 *              /auth/action?mode=resetPassword&oobCode=XXX
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode, verifyPasswordResetCode, confirmPasswordReset, signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight, KeyRound, Eye, EyeOff } from 'lucide-react';
import { AuthCardTitle, AuthShell } from '../components/auth/AuthShell';

type ActionState = 'loading' | 'success' | 'error' | 'no_code' | 'reset_form' | 'reset_success';

interface ErrorInfo {
  title: string;
  message: string;
  action: 'resend' | 'login' | 'signup' | 'support' | 'retry';
}

function getErrorInfo(errorCode: string): ErrorInfo {
  switch (errorCode) {
    case 'auth/expired-action-code':
      return {
        title: 'Link Expired',
        message: 'This verification link has expired. We\'ll send you a new one.',
        action: 'resend',
      };
    case 'auth/invalid-action-code':
      return {
        title: 'Invalid or Used Link',
        message: 'This link has already been used or is no longer valid.',
        action: 'login',
      };
    case 'auth/user-disabled':
      return {
        title: 'Account Disabled',
        message: 'This account has been disabled. Please contact support.',
        action: 'support',
      };
    case 'auth/user-not-found':
      return {
        title: 'Account Not Found',
        message: 'No account was found for this verification link.',
        action: 'signup',
      };
    default:
      return {
        title: 'Something Went Wrong',
        message: 'We couldn\'t process this verification link. Please try again.',
        action: 'retry',
      };
  }
}

function getInviteToken(searchParams: URLSearchParams): string {
  const directToken = searchParams.get('inviteToken');
  if (directToken) return directToken;

  const continueUrl = searchParams.get('continueUrl');
  if (!continueUrl) return '';

  try {
    return new URL(continueUrl).searchParams.get('inviteToken') || '';
  } catch {
    return '';
  }
}

export const AuthAction: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, sendVerificationEmail, reloadUser } = useAuth();

  const [state, setState] = useState<ActionState>('loading');
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength checks
  const passwordChecks = {
    length: newPassword.length >= 6,
    hasLetter: /[a-zA-Z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
  };
  const allChecksPassed = passwordChecks.length && passwordChecks.hasLetter && passwordChecks.hasNumber;

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const inviteToken = getInviteToken(searchParams);
  const [inviteCompleted, setInviteCompleted] = useState(false);

  useEffect(() => {
    if (!oobCode || !mode) {
      setState('no_code');
      return;
    }

    if (mode === 'verifyEmail') {
      processVerification(oobCode);
    } else if (mode === 'resetPassword') {
      processPasswordResetCode(oobCode);
    } else {
      setState('no_code');
    }
  }, [oobCode, mode]);

  async function processVerification(code: string) {
    setState('loading');
    try {
      await applyActionCode(auth, code);

      // Reload user to pick up verified status
      if (auth.currentUser) {
        await auth.currentUser.reload();
      }
      await reloadUser();

      setState('success');

      // Auto-redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 3000);
    } catch (err: any) {
      console.error('[AuthAction] Verification failed:', err.code, err.message);

      // If "invalid-action-code", check if user is already verified
      if (err.code === 'auth/invalid-action-code' && auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          setState('success');
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 3000);
          return;
        }
      }

      const info = getErrorInfo(err.code || 'unknown');
      setErrorInfo(info);
      setState('error');

      // Auto-resend for expired codes
      if (err.code === 'auth/expired-action-code' && user) {
        handleResend();
      }
    }
  }

  async function processPasswordResetCode(code: string) {
    setState('loading');
    try {
      // Verify the reset code is valid and get the associated email
      const email = await verifyPasswordResetCode(auth, code);
      setResetEmail(email);
      setState('reset_form');
    } catch (err: any) {
      console.error('[AuthAction] Password reset code verification failed:', err.code, err.message);
      const info = getErrorInfo(err.code || 'unknown');
      // Customize error messages for password reset context
      if (err.code === 'auth/expired-action-code') {
        info.message = 'This password reset link has expired. Please request a new one from the login page.';
        info.action = 'login';
      } else if (err.code === 'auth/invalid-action-code') {
        info.message = 'This password reset link has already been used or is no longer valid. Please request a new one.';
        info.action = 'login';
      }
      setErrorInfo(info);
      setState('error');
    }
  }

  async function handlePasswordReset(e: React.FormEvent) {
    e.preventDefault();
    setResetError('');

    if (newPassword.length < 6) {
      setResetError('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setResetError('Passwords do not match.');
      return;
    }

    if (!oobCode) {
      setResetError('Missing reset code. Please use the link from your email.');
      return;
    }

    setResetLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      if (inviteToken) {
        const credential = await signInWithEmailAndPassword(auth, resetEmail, newPassword);
        const completeOrganizationInvite = httpsCallable<{ inviteToken: string }, { success: boolean }>(
          functions,
          'completeOrganizationInvite'
        );
        const completeDomainInvite = httpsCallable<{ inviteToken: string }, { success: boolean }>(
          functions,
          'completeDomainUserInvite'
        );

        try {
          await completeOrganizationInvite({ inviteToken });
        } catch (inviteErr: any) {
          if (String(inviteErr?.code || '').includes('not-found')) {
            await completeDomainInvite({ inviteToken });
          } else {
            throw inviteErr;
          }
        }

        await credential.user.getIdToken(true);
        await reloadUser();
        setInviteCompleted(true);
      }
      setState('reset_success');
    } catch (err: any) {
      console.error('[AuthAction] Password reset failed:', err.code, err.message);
      if (err.code === 'auth/expired-action-code') {
        setResetError('This reset link has expired. Please request a new one from the login page.');
      } else if (err.code === 'auth/weak-password') {
        setResetError('Password is too weak. Please use at least 6 characters with a mix of letters and numbers.');
      } else {
        setResetError(err.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  }

  async function handleResend() {
    if (!user) {
      navigate('/login');
      return;
    }
    setResending(true);
    setResendMessage('');
    try {
      await sendVerificationEmail(user);
      setResendMessage('A new verification email has been sent. Check your inbox.');
    } catch (err: any) {
      console.error('[AuthAction] Resend failed:', err);
      setResendMessage('Failed to resend. Please try again from the verification page.');
    } finally {
      setResending(false);
    }
  }

  function handleAction() {
    if (!errorInfo) return;
    switch (errorInfo.action) {
      case 'resend':
        handleResend();
        break;
      case 'login':
        navigate('/login');
        break;
      case 'signup':
        navigate('/signup');
        break;
      case 'support':
        // Navigate to login with a message
        navigate('/login', { state: { message: 'Please contact support for account assistance.' } });
        break;
      case 'retry':
        if (oobCode) {
          processVerification(oobCode);
        } else {
          navigate('/verify-email');
        }
        break;
    }
  }

  function getActionLabel(): string {
    if (!errorInfo) return '';
    switch (errorInfo.action) {
      case 'resend': return 'Resend Verification Email';
      case 'login': return 'Go to Login';
      case 'signup': return 'Create Account';
      case 'support': return 'Go to Login';
      case 'retry': return 'Try Again';
      default: return 'Continue';
    }
  }

  return (
    <AuthShell>
      <Card className="w-full border-border bg-card shadow-[0_15px_35px_-5px_rgba(0,0,0,0.15)]">
        {state === 'loading' && (
          <>
            <CardHeader className="space-y-3">
              <div className="mb-1 flex h-12 w-12 items-center justify-center bg-muted text-primary">
                <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none" />
              </div>
              <AuthCardTitle className="text-3xl font-normal text-foreground">
                {mode === 'resetPassword' ? 'Preparing password reset...' : 'Verifying your email...'}
              </AuthCardTitle>
              <CardDescription className="text-base">
                {mode === 'resetPassword'
                  ? 'Please wait while we verify your reset link.'
                  : 'Please wait while we process your verification link.'}
              </CardDescription>
            </CardHeader>
          </>
        )}

        {state === 'success' && (
          <>
            <CardHeader className="space-y-3">
              <div className="mb-1 flex h-12 w-12 items-center justify-center bg-status-success text-status-success-foreground">
                <CheckCircle className="h-6 w-6" />
              </div>
              <AuthCardTitle className="text-3xl font-normal text-foreground">Email verified</AuthCardTitle>
              <CardDescription className="text-base">
                Your email has been successfully verified. Redirecting to dashboard...
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate('/dashboard', { replace: true })}
                className="w-full h-11 font-medium shadow-md hover:shadow-lg transition-smooth"
              >
                Go to Dashboard
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </>
        )}

        {state === 'error' && errorInfo && (
          <>
            <CardHeader className="space-y-3">
              <div className="mb-1 flex h-12 w-12 items-center justify-center bg-status-error text-status-error-foreground">
                <XCircle className="h-6 w-6" />
              </div>
              <AuthCardTitle className="text-3xl font-normal text-foreground">{errorInfo.title}</AuthCardTitle>
              <CardDescription className="text-base">
                {errorInfo.message}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {resendMessage && (
                <Alert className="border-status-success-foreground/20 bg-status-success text-status-success-foreground">
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>{resendMessage}</AlertDescription>
                </Alert>
              )}

              <Button
                onClick={handleAction}
                className="w-full h-11 font-medium shadow-md hover:shadow-lg transition-smooth"
                disabled={resending}
              >
                {resending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                    Sending...
                  </>
                ) : (
                  getActionLabel()
                )}
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate('/verify-email')}
                className="w-full h-11 font-medium"
              >
                Go to Verification Page
              </Button>
            </CardContent>
          </>
        )}

        {state === 'reset_form' && (
          <>
            <CardHeader className="space-y-3">
              <div className="mb-1 flex h-12 w-12 items-center justify-center bg-muted text-primary">
                <KeyRound className="h-6 w-6" />
              </div>
              <AuthCardTitle className="text-3xl font-normal text-foreground">Reset your password</AuthCardTitle>
              <CardDescription className="text-base">
                Enter a new password for <strong>{resetEmail}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordReset} className="space-y-4">
                {resetError && (
                  <Alert variant="destructive">
                    <AlertDescription>{resetError}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      disabled={resetLoading}
                      className="h-11 pr-10"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-1 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {/* Password strength indicator */}
                  {newPassword.length > 0 && (
                    <div className="space-y-1 pt-1">
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className={passwordChecks.length ? 'text-status-success-foreground' : 'text-muted-foreground'}>
                          {passwordChecks.length ? '\u2713' : '\u2022'} 6+ characters
                        </span>
                        <span className="text-muted-foreground/40">|</span>
                        <span className={passwordChecks.hasLetter ? 'text-status-success-foreground' : 'text-muted-foreground'}>
                          {passwordChecks.hasLetter ? '\u2713' : '\u2022'} Has letter
                        </span>
                        <span className="text-muted-foreground/40">|</span>
                        <span className={passwordChecks.hasNumber ? 'text-status-success-foreground' : 'text-muted-foreground'}>
                          {passwordChecks.hasNumber ? '\u2713' : '\u2022'} Has number
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmNewPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                      minLength={6}
                      disabled={resetLoading}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-1 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      aria-label={showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {confirmNewPassword.length > 0 && confirmNewPassword !== newPassword && (
                    <p className="text-xs text-destructive">Passwords do not match</p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 font-medium shadow-md hover:shadow-lg transition-smooth"
                  disabled={resetLoading || !allChecksPassed}
                >
                  {resetLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  This link expires 1 hour after it was sent.{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-primary hover:underline"
                  >
                    Request a new one
                  </button>
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="w-full h-9 text-sm text-muted-foreground"
                >
                  Back to Login
                </Button>
              </form>
            </CardContent>
          </>
        )}

        {state === 'reset_success' && (
          <>
            <CardHeader className="space-y-3">
              <div className="mb-1 flex h-12 w-12 items-center justify-center bg-status-success text-status-success-foreground">
                <CheckCircle className="h-6 w-6" />
              </div>
              <AuthCardTitle className="text-3xl font-normal text-foreground">Password reset</AuthCardTitle>
              <CardDescription className="text-base">
                {inviteCompleted
                  ? 'Your account is ready. You can now access ReturnCatalyst.'
                  : 'Your password has been successfully updated. You can now sign in with your new password.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate(inviteCompleted ? '/dashboard' : '/login', { replace: true })}
                className="w-full h-11 font-medium shadow-md hover:shadow-lg transition-smooth"
              >
                {inviteCompleted ? 'Go to Dashboard' : 'Go to Login'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </>
        )}

        {state === 'no_code' && (
          <>
            <CardHeader className="space-y-3">
              <div className="mb-1 flex h-12 w-12 items-center justify-center bg-status-warning text-status-warning-foreground">
                <Mail className="h-6 w-6" />
              </div>
              <AuthCardTitle className="text-3xl font-normal text-foreground">No verification code</AuthCardTitle>
              <CardDescription className="text-base">
                This page needs a verification link to work. Please check your email and click the verification link.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={() => navigate('/verify-email')}
                className="w-full h-11 font-medium shadow-md hover:shadow-lg transition-smooth"
              >
                Go to Verification Page
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="w-full h-11 font-medium"
              >
                Go to Login
              </Button>
            </CardContent>
          </>
        )}
      </Card>
    </AuthShell>
  );
};
